import { Fragment } from 'react';
import type { ReactNode } from 'react';
import { Card, SectionLabel } from '@/components/ui';
import { founders } from '@/content/founders';
import type { Founder } from '@/content/founders';
import { useIsMobile } from '@/hooks/isMobile';
import { C } from '@/theme/tokens';

/**
 * Renders inline markdown-ish formatting used in founder bullets:
 * `**bold**` and `*italic*`. Kept intentionally small — just enough to
 * style book/anthology titles and emphasised terms from the design.
 */
function formatText(text: string): ReactNode[] {
  const tokens = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
  return tokens
    .filter(Boolean)
    .map((token, i) => {
      if (token.startsWith('**') && token.endsWith('**')) {
        return (
          <strong key={i} style={{ color: '#fff', fontWeight: 700 }}>
            {token.slice(2, -2)}
          </strong>
        );
      }
      if (token.startsWith('*') && token.endsWith('*')) {
        return <em key={i}>{token.slice(1, -1)}</em>;
      }
      return <Fragment key={i}>{token}</Fragment>;
    });
}

function FounderColumn({ founder }: { founder: Founder }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Profile card */}
      <Card style={{ padding: 28 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 24,
            flexWrap: 'wrap',
          }}
        >
          <img
            src={founder.photo}
            alt={founder.name}
            style={{
              width: 130,
              height: 130,
              borderRadius: '50%',
              objectFit: 'cover',
              border: `3px solid ${C.gold}`,
              background: C.gold,
              flexShrink: 0,
            }}
          />
          <div style={{ flex: 1, minWidth: 200 }}>
            <p
              style={{
                fontFamily: "'Cinzel',serif",
                fontSize: 'clamp(1.3rem,2.8vw,1.8rem)',
                fontWeight: 900,
                color: '#fff',
                margin: 0,
                lineHeight: 1.1,
              }}
            >
              {founder.name}
            </p>
            <div
              style={{
                fontFamily: "'Cinzel',serif",
                fontSize: 15,
                fontWeight: 700,
                color: '#fff',
                marginTop: 8,
              }}
            >
              {founder.role}
            </div>
            <div
              style={{
                fontSize: 14,
                color: C.whiteSoft,
                marginTop: 8,
              }}
            >
              {founder.tagline}
            </div>
          </div>
        </div>
      </Card>

      {/* Bio bullets card */}
      <Card style={{ padding: 28, flex: 1 }}>
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {founder.bullets.map((bullet, i) => (
            <li
              key={i}
              style={{
                display: 'flex',
                gap: 12,
                marginBottom: i === founder.bullets.length - 1 ? 0 : 16,
              }}
            >
              <span style={{ color: C.gold, fontSize: 14, lineHeight: 1.6 }}>•</span>
              <span style={{ fontSize: 15, color: C.whiteSoft, lineHeight: 1.6 }}>
                {formatText(bullet)}
              </span>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}

/**
 * "About Us" — meet the founders. Two-column layout on desktop (Adam / Zach),
 * stacking vertically on mobile. Mirrors the supplied design 1:1.
 */
export function AboutUsSection() {
  const isMobile = useIsMobile();

  return (
    <section
      id="about"
      style={{
        background: C.bgDeep,
        padding: '36px 24px',
      }}
    >
      <div style={{ maxWidth: 1080, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 56 }}>
          <SectionLabel>About Us</SectionLabel>
        <p
          style={{
            fontSize: 16,
            color: 'rgba(255,255,255,0.65)',
            lineHeight: 1.3,
            maxWidth: 640,
            margin: '0 auto 40px',
          }}
          >
            <span style={{ fontSize: '24px' }}>Long time friends, GMs, and authors, Adam &amp; Zach bring strong technical and
            organizational skills, life-long creativity, and deep love of TTRPGs going back decades.</span>
          </p>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: 32,
            alignItems: 'stretch',
          }}
        >
          {founders.map((founder) => (
            <FounderColumn key={founder.name} founder={founder} />
          ))}
        </div>
      </div>
    </section>
  );
}
